// PromptDialog — window.prompt 대체 컴포넌트.
// ConfirmDialog 와 동일 패턴: Provider + usePrompt hook + Promise 반환.
//
// 사용:
//   const prompt = usePrompt();
//   const value = await prompt({ title: '...', message: '...', defaultValue: '...', placeholder: '...' });
//   if (value === null) return; // 취소
//   // value 는 string

import React, { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { dialogHost } from '../utils/popoutWindow';
import './ConfirmDialog.css';

const PromptContext = createContext(null);

export const usePrompt = () => useContext(PromptContext);

export const PromptProvider = ({ children }) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState(null);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  // inputType: 'password' 면 입력을 가린다(내보내기 암호 등, 2026-10-07). 기본은 text
  const prompt = useCallback(({ title, message, defaultValue, placeholder, confirmText, cancelText, inputType }) => {
    return new Promise((resolve) => {
      setValue(defaultValue || '');
      setDialog({ title, message, placeholder, confirmText, cancelText, inputType, resolve, host: dialogHost() });
    });
  }, []);

  const handleClose = (result) => {
    if (dialog?.resolve) dialog.resolve(result);
    setDialog(null);
    setValue('');
  };

  useEffect(() => {
    if (dialog && inputRef.current) {
      // 다음 tick 에 focus + select (createPortal 마운트 후)
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [dialog]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClose(value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClose(null);
    }
  };

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      {dialog && createPortal(
        <div className="confirm-overlay" onClick={() => handleClose(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-header">{dialog.title || t('common:dlg.input')}</div>
            <div className="confirm-body">
              {dialog.message && <div style={{ marginBottom: 8 }}>{dialog.message}</div>}
              <input
                ref={inputRef}
                type={dialog.inputType === 'password' ? 'password' : 'text'}
                autoComplete={dialog.inputType === 'password' ? 'new-password' : undefined}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={dialog.placeholder || ''}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid var(--color-border, #d1d5db)',
                  borderRadius: 4,
                  fontSize: 13,
                  boxSizing: 'border-box',
                  background: 'var(--color-bg, #fff)',
                  color: 'var(--color-text, #1f2937)',
                }}
              />
            </div>
            <div className="confirm-footer">
              <button className="confirm-btn" onClick={() => handleClose(null)}>
                {dialog.cancelText || t('common:dlg.cancel')}
              </button>
              <button
                className="confirm-btn confirm-btn-primary"
                onClick={() => handleClose(value)}
              >
                {dialog.confirmText || t('common:dlg.ok')}
              </button>
            </div>
          </div>
        </div>,
        dialog.host || document.body,
      )}
    </PromptContext.Provider>
  );
};

export default PromptProvider;
